import React, { useState, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { FaCopy } from 'react-icons/fa';
import './QRSectionEditor.css';
import { toast } from 'react-toastify';

export default function QRSectionEditor({ 
  qrElement, 
  qrRef,
  onCopyAll,
  className = ""
}) {
  // Fixed header and footer sections only
  const [sections, setSections] = useState([
    {
      id: 'header-default',
      content: '',
      position: 'before',
      isDefault: true,
      label: 'Header'
    },
    {
      id: 'footer-default',
      content: '',
      position: 'after',
      isDefault: true,
      label: 'Footer'
    }
  ]);

  const updateSection = (id, content) => {
    setSections(prev => prev.map(section => 
      section.id === id ? { ...section, content } : section
    ));
  };

  const convertHtmlToRtf = (html) => {
    let rtf = '{\\rtf1\\ansi\\deff0 {\\fonttbl {\\f0 Times New Roman;}}';
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : html;
    
    // Simple HTML to RTF conversion
    let rtfContent = bodyContent
      // Remove HTML tags but keep structure
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\\par\\b $1\\b0\\par')
      .replace(/<div[^>]*class="[^"]*section-content[^"]*"[^>]*>/gi, '\\par')
      .replace(/<\/div>/gi, '\\par')
      .replace(/<div[^>]*class="[^"]*qr-container[^"]*"[^>]*>/gi, '\\par\\qc')
      .replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, '{\\*\\shppict{\\pict\\pngblip\\picw3000\\pich3000 [Image: QR Code]}}')
      .replace(/<p[^>]*>/gi, '\\par')
      .replace(/<\/p>/gi, '\\par')
      .replace(/<br[^>]*>/gi, '\\par')
      .replace(/<strong>(.*?)<\/strong>/gi, '\\b $1\\b0')
      .replace(/<b>(.*?)<\/b>/gi, '\\b $1\\b0')
      .replace(/<em>(.*?)<\/em>/gi, '\\i $1\\i0')
      .replace(/<i>(.*?)<\/i>/gi, '\\i $1\\i0')
      // Clean up remaining HTML tags
      .replace(/<[^>]*>/g, '')
      // Handle special characters
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    rtf += rtfContent + '}';
    return rtf;
  };

  // Detect if device is iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // iOS-friendly copy function
  const copyToClipboardIOS = async (text, html = null) => {
    try {
      // Method 1: Try modern clipboard API (works on newer iOS versions)
      if (navigator.clipboard && window.isSecureContext) {
        if (html && !isIOS()) {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([text], { type: 'text/plain' })
            })
          ]);
        } else {
          // iOS: only plain text (more reliable)
          await navigator.clipboard.writeText(text);
        }
        return { success: true, method: 'modern', format: html && !isIOS() ? 'html' : 'text' };
      }
      
      // Method 2: iOS fallback - create textarea and select
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      textArea.setAttribute('readonly', 'readonly');
      
      document.body.appendChild(textArea);
      
      // Focus and select - important for iOS
      textArea.focus();
      textArea.setSelectionRange(0, textArea.value.length);
      
      // For iOS: need to trigger selection differently
      if (isIOS()) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      }
      
      // Copy using execCommand (still works on iOS)
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (success) {
        return { success: true, method: 'execCommand', format: 'text' };
      } else {
        throw new Error('execCommand failed');
      }
      
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleCopyAll = async () => {
    let allContentText = '';
    let allContentHTML = '';
    
    // Create complete HTML document with styling
    let fullHTML = `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <style>
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                    line-height: 1.6; 
                    margin: 20px; 
                    color: #333;
                }
                h3 { 
                    color: #2563eb; 
                    border-bottom: 2px solid #e5e7eb; 
                    padding-bottom: 8px;
                    margin-top: 24px;
                    margin-bottom: 16px;
                }
                .qr-container { 
                    text-align: center; 
                    margin: 30px 0; 
                    padding: 20px;
                    background-color: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                }
                .qr-container img { 
                    max-width: 300px; 
                    height: auto; 
                    border-radius: 8px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }
                .section-content {
                    margin-bottom: 20px;
                    padding: 16px;
                    background-color: #ffffff;
                    border-radius: 6px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                .before-section {
                    border-left: 4px solid #3b82f6;
                }
                .after-section {
                    border-left: 4px solid #10b981;
                }
            </style>
        </head>
        <body>
    `;
    
    // Separate sections by position
    const beforeSections = sections.filter(section => section.position === 'before');
    const afterSections = sections.filter(section => section.position === 'after');
    
    // Add sections before QR code
    beforeSections.forEach((section, index) => {
      if (section.content.trim()) {
        const textContent = section.content.replace(/<[^>]*>/g, '').trim();
        if (textContent) {
          allContentText += textContent + '\n\n';
        }
        
        fullHTML += `<div class="section-content before-section">`;
        fullHTML += `<div>${section.content}</div>`;
        fullHTML += `</div>\n`;
      }
    });
    
    
    if (qrElement) {
      // Try to capture QR on all devices, but with different approaches
      try {
        let qrContainer = null;
        let qrImageHTML = '';
        
        // Method 1: Try to find QR element through qrRef
        if (qrRef && qrRef.current) {
          qrContainer = qrRef.current.querySelector('svg, canvas, img');
        }
        
        // Method 2: Search globally if not found in ref
        if (!qrContainer) {
          const selectors = [
            'svg[width][height]',
            'canvas[width][height]', 
            '.qr-section-editor svg',
            '.qr-section-editor canvas',
            '.flex.justify-center svg',
            '.flex.justify-center canvas',
            'div[class*="qr"] svg',
            'div[class*="qr"] canvas'
          ];
          
          for (const selector of selectors) {
            const elements = document.querySelectorAll(selector);
            for (const element of elements) {
              const width = element.getAttribute('width') || element.clientWidth;
              const height = element.getAttribute('height') || element.clientHeight;
              if (width >= 200 && height >= 200) {
                qrContainer = element;
                break;
              }
            }
            if (qrContainer) break;
          }
        }
        
        if (qrContainer) {
          const tagName = qrContainer.tagName.toLowerCase();
          
          if (tagName === 'svg') {
            try {
              // Convert SVG to data URL - works on most modern browsers including mobile
              const svgData = new XMLSerializer().serializeToString(qrContainer);
              
              if (svgData.length < 100) {
                throw new Error('SVG data too short');
              }
              
              // Fix SVG namespace issues
              const fixedSvgData = svgData.includes('xmlns=') ? svgData : 
                svgData.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
              
              // For mobile compatibility: try both SVG and PNG approaches
              const svgBase64 = btoa(unescape(encodeURIComponent(fixedSvgData)));
              const svgDataUrl = `data:image/svg+xml;base64,${svgBase64}`;
              
              // Try PNG conversion for better mobile compatibility
              if (!isIOS()) {
                try {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  const img = new Image();
                  
                  await new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(() => reject(new Error('PNG conversion timeout')), 3000);
                    
                    img.onload = () => {
                      clearTimeout(timeoutId);
                      try {
                        canvas.width = img.width || 300;
                        canvas.height = img.height || 300;
                        ctx.fillStyle = '#ffffff'; // White background
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(img, 0, 0);
                        
                        const pngDataUrl = canvas.toDataURL('image/png', 0.9);
                        qrImageHTML = `<img src="${pngDataUrl}" alt="QR Code" style="max-width: 300px; height: auto; display: block; margin: 0 auto;" />`;
                        resolve();
                      } catch (drawError) {
                        reject(drawError);
                      }
                    };
                    
                    img.onerror = () => {
                      clearTimeout(timeoutId);
                      reject(new Error('Image load failed'));
                    };
                    
                    img.src = svgDataUrl;
                  });
                } catch (pngError) {
                  // Fallback to SVG if PNG conversion fails
                  console.warn('PNG conversion failed, using SVG:', pngError);
                  qrImageHTML = `<img src="${svgDataUrl}" alt="QR Code" style="max-width: 300px; height: auto; display: block; margin: 0 auto;" />`;
                }
              } else {
                // For iOS: use SVG directly (often works better)
                qrImageHTML = `<img src="${svgDataUrl}" alt="QR Code" style="max-width: 300px; height: auto; display: block; margin: 0 auto;" />`;
              }
              
            } catch (svgError) {
              console.error('SVG conversion failed:', svgError);
              throw svgError;
            }
            
          } else if (tagName === 'canvas') {
            try {
              const canvas = qrContainer;
              const ctx = canvas.getContext('2d');
              
              // Check if canvas has content
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const hasContent = imageData.data.some(pixel => pixel !== 0);
              
              if (!hasContent) {
                throw new Error('Canvas is empty');
              }
              
              const dataURL = canvas.toDataURL('image/png', 0.9);
              
              if (dataURL === 'data:,' || dataURL.length < 100) {
                throw new Error('Canvas data URL invalid');
              }
              
              qrImageHTML = `<img src="${dataURL}" alt="QR Code" style="max-width: 300px; height: auto; display: block; margin: 0 auto;" />`;
              
            } catch (canvasError) {
              console.error('Canvas conversion failed:', canvasError);
              throw canvasError;
            }
            
          } else if (tagName === 'img') {
            try {
              if (!qrContainer.src || qrContainer.src.length < 10) {
                throw new Error('Image src invalid');
              }
              
              qrImageHTML = `<img src="${qrContainer.src}" alt="QR Code" style="max-width: 300px; height: auto; display: block; margin: 0 auto;" />`;
            } catch (imgError) {
              console.error('IMG processing failed:', imgError);
              throw imgError;
            }
          }
          
          // Add QR to content
          if (qrImageHTML) {
            allContentText += '\n[QR CODE]\n\n';
            fullHTML += `<div class="qr-container">`;
            fullHTML += qrImageHTML;
            fullHTML += `</div>\n`;
          } else {
            throw new Error('No valid QR image generated');
          }
          
        } else {
          throw new Error('QR container not found');
        }
        
      } catch (error) {
        console.error('❌ QR capture failed:', error);
        
        // Fallback for all devices
        allContentText += '\n[QR CODE - Xem trên màn hình]\n\n';
        fullHTML += `<div class="qr-container">`;
        
        if (isIOS()) {
          fullHTML += `<p style="text-align: center; color: #666; padding: 40px; border: 2px dashed #ccc; border-radius: 8px;">📱 [QR Code - Hiển thị trên thiết bị iOS]</p>`;
        } else {
          fullHTML += `<p style="text-align: center; color: #ef4444; padding: 40px; border: 2px dashed #ccc; border-radius: 8px;">⚠️ [Không thể capture QR: ${error.message}]</p>`;
        }
        
        fullHTML += `</div>\n`;
      }
    }
    
    // Add sections after QR code
    afterSections.forEach((section, index) => {
      if (section.content.trim()) {
        // For text content: only add the pure text without titles
        const textContent = section.content.replace(/<[^>]*>/g, '').trim();
        if (textContent) {
          allContentText += textContent + '\n\n';
        }
        
        // For HTML: keep the structured format with titles
        fullHTML += `<div class="section-content after-section">`;
        fullHTML += `<div>${section.content}</div>`;
        fullHTML += `</div>\n`;
      }
    });
    
    // Close HTML document
    fullHTML += `
</body>
</html>`;
    
    // Use the new iOS-friendly copy function
    const result = await copyToClipboardIOS(allContentText, fullHTML);
    
    if (result.success) {
      const deviceType = isIOS() ? '📱 iOS' : '💻 Desktop';
      const formatType = result.format === 'html' ? 'HTML + Text' : 'Text';

      toast.success(`Đã sao chép thành công!`);
    } else {
      toast.error(`Tự động copy không thành công!`);
    }
    
    if (onCopyAll) {
      onCopyAll(allContentText, { sections, htmlContent: fullHTML });
    }
  };

  const editorConfig = {
    base_url: '/tinymce',
    suffix: '.min',
    height: 250,
    menubar: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'charmap', 
      'preview', 'anchor', 'searchreplace', 'visualblocks', 'code', 
      'fullscreen', 'insertdatetime', 'media', 'help', 'wordcount',
    ],
    toolbar:
      'undo redo | formatselect | bold italic underline strikethrough | ' +
      'alignleft aligncenter alignright alignjustify | ' +
      'bullist numlist outdent indent | link | ' +
      'forecolor backcolor | removeformat',
    toolbar_sticky: false,
    content_style: `
      body { font-family:HungHau,Helvetica,Arial,sans-serif; font-size:13px; margin: 10px; }
    `,
    setup: (editor) => {
      // Prevent editor from losing focus when interacting with other elements
      editor.on('blur', (e) => {
        // Check if the blur is due to clicking on drag handle or position toggle
        const relatedTarget = e.relatedTarget || document.activeElement;
        if (relatedTarget && (
          relatedTarget.closest('.drag-handle') || 
          relatedTarget.closest('.position-toggle') ||
          relatedTarget.closest('.section-controls')
        )) {
          // Prevent blur, keep editor focused
          setTimeout(() => {
            editor.focus();
          }, 50);
        }
      });
      
      // Prevent editor from auto-focusing when content changes
      editor.on('init', () => {
        editor.getBody().style.outline = 'none';
      });
    }
  };

  // Get sections in display order (before sections, QR, after sections)
  const beforeSections = sections.filter(section => section.position === 'before');
  const afterSections = sections.filter(section => section.position === 'after');

  return (
    <div className={`qr-section-editor ${className}`}>
      {/* Control Buttons */}
      <div className="flex justify-end gap-2 mb-4 bg-gray-50 rounded-lg border flex-wrap">
        <button
          onClick={handleCopyAll}
          className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm"
          type="button"
        >
          <FaCopy size={12} />
          Copy
        </button>
      </div>

      {/* Header Section */}
      {beforeSections.map((section) => (
        <div 
          key={section.id} 
          className="section-item mb-4 p-3 border border-blue-200 rounded-lg bg-blue-50"
        >
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-blue-700">
                📄 {section.label}
              </h4>
            </div>
          </div>
          <Editor
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            value={section.content}
            init={editorConfig}
            onEditorChange={(content) => updateSection(section.id, content)}
          />
        </div>
      ))}

      {/* QR Code Display Area - passed from parent */}
      {qrElement && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
          <div className="flex justify-center">
            {qrElement}
          </div>
        </div>
      )}

      {/* Footer Section */}
      {afterSections.map((section) => (
        <div 
          key={section.id} 
          className="section-item mb-4 p-3 border border-green-200 rounded-lg bg-green-50"
        >
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-green-700">
                📝 {section.label}
              </h4>
            </div>
          </div>
          <Editor
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            value={section.content}
            init={editorConfig}
            onEditorChange={(content) => updateSection(section.id, content)}
          />
        </div>
      ))}


    </div>
  );
}
